import { SIM_STEP_MS } from '../lib/constants';

/** Frames > 250 ms (Tab im Hintergrund) nicht aufholen — Spirale vermeiden. */
const MAX_FRAME_MS = 250;
/** Watchdog: laeuft rAF nicht mehr (gedrosselter/verdeckter Tab), uebernimmt ein Timer die Simulation. */
const WATCHDOG_INTERVAL_MS = 100;
const RAF_STALL_THRESHOLD_MS = 200;

/**
 * Fixed-Timestep-Loop: update() laeuft deterministisch mit 60 Hz,
 * render() einmal pro Animation-Frame (klassisches Akkumulator-Muster).
 *
 * Browser drosseln requestAnimationFrame in verdeckten/Hintergrund-Tabs
 * bis auf ~1 Hz. Damit die Simulation (Positionen, Proximity-Hinweise,
 * Server-Sync) konsistent weiterlaeuft, springt dann ein setInterval-
 * Watchdog fuer die update-Schritte ein — gerendert wird weiterhin nur
 * in echten Animation-Frames.
 */
export class GameLoop {
  private rafHandle = 0;
  private watchdogHandle: ReturnType<typeof setInterval> | null = null;
  private lastTime = 0;
  private lastRafAt = 0;
  private accumulator = 0;
  private running = false;

  constructor(
    /**
     * `dtMs` ist Simulationszeit und immer `SIM_STEP_MS`; `nowMs` ist die
     * echte Uhr und bleibt ueber alle Nachholschritte eines Frames gleich.
     * Wer etwas drosselt, das die Aussenwelt betrifft — Netzwerk vor allem —,
     * muss `nowMs` nehmen: sonst feuert ein einziger langer Frame mehrere
     * Nachrichten in dieselbe Millisekunde.
     */
    private readonly update: (dtMs: number, nowMs: number) => void,
    private readonly render: (nowMs: number) => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.lastRafAt = this.lastTime;

    const step = (now: number): void => {
      const frameMs = Math.min(now - this.lastTime, MAX_FRAME_MS);
      this.lastTime = now;
      this.accumulator += frameMs;
      while (this.accumulator >= SIM_STEP_MS) {
        this.update(SIM_STEP_MS, now);
        this.accumulator -= SIM_STEP_MS;
      }
    };

    const frame = (now: number): void => {
      if (!this.running) return;
      this.lastRafAt = now;
      step(now);
      this.render(now);
      this.rafHandle = requestAnimationFrame(frame);
    };
    this.rafHandle = requestAnimationFrame(frame);

    this.watchdogHandle = setInterval(() => {
      const now = performance.now();
      // rAF laeuft normal -> nichts tun
      if (now - this.lastRafAt < RAF_STALL_THRESHOLD_MS) return;
      step(now);
    }, WATCHDOG_INTERVAL_MS);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
    if (this.watchdogHandle !== null) clearInterval(this.watchdogHandle);
    this.watchdogHandle = null;
  }
}
